import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
 Dialog,
 DialogTitle,
 DialogContent, 
 DialogActions,
 Button,
 Typography,
 IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './GuidedTour.module.sass';

// image and video imports
import addWalletVideo from 'assets/load_circle.mov';
import securityScoreImage from 'assets/security_score_detail.png';
import discoveryTokenDetail from 'assets/discovery_token_detail.png';
import discoverAssetsVideo from 'assets/discovery_demo_short.mov';
import assetNavigationVideo from 'assets/asset_navigation.mov';
import portfolioWorkflow from 'assets/portfolio_workflow.mov';
import assetSubscriptionVideo from 'assets/asset_subscribe_button.mov';

interface TourStep {
  targetId: string;
  title: string;
  description: string;
  videoUrl?: string;
  imageUrl?: string;
  page?: string;
}

interface GuidedTourProps {
 onComplete?: () => void;
}

const GuidedTour: React.FC<GuidedTourProps> = ({ onComplete }) => {
 const location = useLocation();
 const navigate = useNavigate();
 const [currentStep, setCurrentStep] = useState(0);
 const [isOpen, setIsOpen] = useState(true);
 const [dialogPosition, setDialogPosition] = useState<'left' | 'right'>('right');

 const allSteps: TourStep[] = [
   {
     targetId: 'add-wallet-button',
     title: 'Wallet Manager',
     description: 'Add in your EVM wallets to create your portfolio. Track one wallet for free, or add multiple as a Pro user.',
     videoUrl: addWalletVideo,
     page: '/'
   },
   {
     targetId: 'security-score-header',
     title: 'Security Score',
     description: 'This column shows the security rating for each asset in your portfolio. Click the security button for more detailed insights.',
     imageUrl: securityScoreImage,
     page: '/' 
   },
   {
    targetId: 'pnl-transactions',
    title: 'Profit & Loss Statement',
    description: 'View your profit and loss, average entry / exit price, and total dollar amount invested.',
    page: '/'
  },
  {
    targetId: 'premium-filters',
    title: 'Premium Portfolio Filters',
    description: 'Unlock advanced portfolio filtering capabilities with our PRO subscription. Whether you want to diversify your portfolio, or find assets to juice your returns, we have personalized asset filters just for you.',
    videoUrl: discoverAssetsVideo,
    page: '/discovery'
  },
  {
    targetId: 'discovery-token-expand-link',
    title: 'Token Details', 
    description: 'Click the expand button to get detailed insights of tokens and how they could impact your portfolio.',
    imageUrl: discoveryTokenDetail,
    page: '/discovery'
  },
  {
    targetId: 'portfolio-metric-row',
    title: 'Portfolio Analytics', 
    description: 'Navigate through key portfolio metrics to understand performance relative to the market. Adjust date ranges via the date input on the sidebar.',
    videoUrl: portfolioWorkflow,
    page: '/portfolio'
  },
  {
    targetId: 'historical-holder-count',
    title: 'Asset Analysis', 
    description: 'Navigate through the assets you own by selecting a holder count button. Click expand for a detailed chart of the holder count.',
    videoUrl: assetNavigationVideo,
    page: '/asset-analysis'
  },
  {
    targetId: 'asset-subscription',
    title: 'Asset Notifications', 
    description: 'Click the bell icon to subscribe to trade notifications for a given asset. Astrotek automatically filters for anomalous trades based on historical data so you only see meaningful transactions..',
    videoUrl: assetSubscriptionVideo,
    page: '/asset-analysis'
  }
 ];

 const handleNext = () => {
   if (currentStep < allSteps.length - 1) {
     const nextStep = allSteps[currentStep + 1];
     if (nextStep.page && nextStep.page !== location.pathname) {
       navigate(nextStep.page);
     }
     setCurrentStep(currentStep + 1);
   } else {
     setIsOpen(false);
     onComplete?.();
   }
 };

 const handlePrevious = () => {
   if (currentStep > 0) {
     const prevStep = allSteps[currentStep - 1];
     if (prevStep.page && prevStep.page !== location.pathname) {
       navigate(prevStep.page);
     }
     setCurrentStep(currentStep - 1);
   }
 };

 const handleClose = () => {
   setIsOpen(false);
   onComplete?.();
 };

 useEffect(() => {
  if (isOpen && allSteps[currentStep]?.targetId) {
    const currentStepData = allSteps[currentStep];
    
    // Create a unique style ID for this highlight instance
    const styleId = `highlight-style-${currentStepData.targetId}`;
    
    // Function to apply highlighting
    const applyHighlight = (element: HTMLElement) => {
      // Remove any existing highlight style to prevent duplicates
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }

      // Create keyframes style
      const keyframes = `
        @keyframes pulse {
          0% { 
            box-shadow: 
              0 0 0 4px rgba(115, 246, 255, 0.8),
              0 0 20px rgba(115, 246, 255, 0.8),
              0 0 40px rgba(115, 246, 255, 0.4);
          }
          50% { 
            box-shadow: 
              0 0 0 6px #73F6FF,
              0 0 30px rgba(115, 246, 255, 0.9),
              0 0 50px rgba(115, 246, 255, 0.6);
          }
          100% { 
            box-shadow: 
              0 0 0 4px rgba(115, 246, 255, 0.8),
              0 0 20px rgba(115, 246, 255, 0.8),
              0 0 40px rgba(115, 246, 255, 0.4);
          }
        }
        
        .highlight-active {
          box-shadow: 
            0 0 0 4px rgba(115, 246, 255, 0.8),
            0 0 20px rgba(115, 246, 255, 0.8),
            0 0 40px rgba(115, 246, 255, 0.4) !important;
          animation: pulse 2s infinite !important;
          position: relative !important;
          z-index: 1000 !important;
          transition: all 0.3s ease !important;
        }
      `;
      
      // Add styles to document head
      const styleSheet = document.createElement('style');
      styleSheet.id = styleId;
      styleSheet.textContent = keyframes;
      document.head.appendChild(styleSheet);
      
      // Apply highlight class
      element.classList.add('highlight-active');
      
      // Scroll after a brief delay to ensure smooth transition
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);

      // Update dialog position
      const rect = element.getBoundingClientRect();
      setDialogPosition(rect.left > window.innerWidth / 2 ? 'left' : 'right');
      
      // Return cleanup function
      return () => {
        element.classList.remove('highlight-active');
        const style = document.getElementById(styleId);
        if (style) {
          style.remove();
        }
      };
    };

    // Function to find and highlight element with retries
    const findAndHighlightElement = (retries = 20, interval = 100) => {
      let cleanup: (() => void) | undefined;
      let attempts = 0;
      let timeoutId: NodeJS.Timeout;

      const attemptHighlight = () => {
        const element = document.getElementById(currentStepData.targetId);
        
        if (element) {
          // Element found, apply highlighting
          if (!element.classList.contains('highlight-active')) {
            cleanup?.(); // Clean up previous highlight if exists
            cleanup = applyHighlight(element);
          }
        } else if (attempts < retries) {
          // Element not found, retry
          attempts++;
          timeoutId = setTimeout(attemptHighlight, interval);
        }
      };

      // Start attempting to highlight
      attemptHighlight();

      // Create a mutation observer to watch for DOM changes
      const observer = new MutationObserver((mutations) => {
        const element = document.getElementById(currentStepData.targetId);
        if (element && !element.classList.contains('highlight-active')) {
          cleanup?.(); // Clean up previous highlight if exists
          cleanup = applyHighlight(element);
        }
      });

      // Start observing the document with the configured parameters
      observer.observe(document.body, { 
        childList: true, 
        subtree: true 
      });

      // Return cleanup function
      return () => {
        cleanup?.();
        clearTimeout(timeoutId);
        observer.disconnect();
      };
    };

    // If page navigation is needed
    if (currentStepData.page && currentStepData.page !== location.pathname) {
      navigate(currentStepData.page);
      // Start checking for element after navigation with a slight delay
      const navigationTimeout = setTimeout(() => {
        return findAndHighlightElement();
      }, 100);
      
      return () => {
        clearTimeout(navigationTimeout);
      };
    } else {
      // If we're already on the right page
      return findAndHighlightElement();
    }
  }
}, [currentStep, isOpen, allSteps, location.pathname, navigate]);

 if (!isOpen) return null;

 return (
  <Dialog
    open={isOpen}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      className: styles.dialogPaper,
      style: {
        position: 'fixed',
        margin: 24,
        [dialogPosition]: 24,
        top: '50%',
        transform: 'translateY(-50%)',
        left: dialogPosition === 'left' ? 24 : 'auto',
        right: dialogPosition === 'right' ? 24 : 'auto'
      }
    }}
  >
    <div className={styles.closeButton}>
      <IconButton onClick={handleClose}>
        <CloseIcon className={styles.closeIcon} />
      </IconButton>
    </div>
    <DialogTitle className={styles.title}>
      {allSteps[currentStep].title}
    </DialogTitle>
    <DialogContent className={styles.content}>
      {allSteps[currentStep].videoUrl && (
        <div className={styles.mediaContainer}>
          <video 
            className={styles.media}
            controls
            autoPlay
            muted  // Required for autoplay to work in most browsers
            loop   // Optional: add this if you want the video to loop
            playsInline
            src={allSteps[currentStep].videoUrl}
          >
            Your browser does not support this video format.
          </video>
        </div>
      )}
      {allSteps[currentStep].imageUrl && !allSteps[currentStep].videoUrl && (
        <div className={styles.mediaContainer}>
          <img 
            className={styles.media}
            src={allSteps[currentStep].imageUrl}
            alt={allSteps[currentStep].title}
          />
        </div>
      )}
      <Typography>
        {allSteps[currentStep].description}
      </Typography>
    </DialogContent>
    <DialogActions className={styles.actions}>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          variant="outlined"
          onClick={handlePrevious}
          disabled={currentStep === 0}
          className={`${styles.button} ${styles.previous}`}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          className={`${styles.button} ${styles.next}`}
        >
          {currentStep === allSteps.length - 1 ? 'Finish' : 'Next'}
        </Button>
      </div>
      <Typography className={styles.stepCount}>
        {currentStep + 1} of {allSteps.length}
      </Typography>
    </DialogActions>
  </Dialog>
 );
};

export default GuidedTour;